<template>
  <CustomTable 
    :loading="loading" 
    :columns="adjustmentColumns" 
    :data="sortedData"
    :itemsPerPageOptions="[5, 10, 20, 50]"
  >
    <template v-slot:employee="{ item }">
      {{ item.employeeName || 'N/A' }}
    </template>
    <template v-slot:adjustmentType="{ item }">
      {{ item.adjustmentType }}
    </template>
    <template v-slot:amount="{ item }">
      {{ formatAmount(item) }}
    </template>
    <template v-slot:reason="{ item }">
      <span class="text-ellipsis" :title="item.reason" style="margin-bottom:-5.5px">
        {{ item.reason }}
      </span>
    </template>
    <template v-slot:receipt="{ item }">
      <a v-if="item.receiptUrl" :href="item.receiptUrl" target="_blank" rel="noopener" class="receipt-link">View</a>
      <span v-else class="muted">-</span>
    </template>
    <template v-slot:addedBy="{ item }">
      {{ item.addedBy || 'N/A' }}
    </template>
    <template v-slot:createdAt="{ item }">
      {{ formatDate(item.createdAt) }}
    </template>
    <template v-slot:action="{ item }">
      <div v-if="canModify(item)" class="action-buttons">
        <div @click="$emit('edit', item)" class="edit-btn btn-status" title="Edit">
          <PencilIcon color="gray" :width="16" :height="16" />
        </div>
        <div @click="$emit('openDeleteModal', item)" class="delete-btn btn-status" title="Delete">
          <TrashIcon color="red" :width="16" :height="16" />
        </div>
      </div>
      <span v-else class="muted" title="Payroll for this month is already processed">Locked</span>
    </template>
  </CustomTable>
</template>

<script>
import CustomTable from "src/components/UIComponents/CustomTable.vue";
import PencilIcon from 'src/assets/icons/PencilIcon.js';
import TrashIcon from 'src/assets/icons/TrashIcon.js';
import { calculatePerDaySalary, calculatePerHourSalary } from "../../../utils/perdaySallaryUtils";

export default {
  name: "AdjustmentTable",
  components: {
    CustomTable,
    PencilIcon,
    TrashIcon
  },
  props: {
    loading: Boolean,
    data: Array,
    employeeList: {
      type: Array,
      default: () => []
    },
    workingDays: {
      type: Array,
      default: () => []
    },
    workingHours: {
      type: Number,
      default: 9
    },
    workingDaysInMonth: {
      type: Number,
      default: 1
    },
    canModify: {
      type: Function,
      default: () => true
    }
  },
  emits: ['edit', 'openDeleteModal'],
  data() {
    return {
      adjustmentColumns: [
        { key: "employee", label: "Employee" },
        { key: "adjustmentType", label: "Adjustment Type" },
        { key: "amount", label: "Amount" },
        { key: "reason", label: "Reason" },
        { key: "receipt", label: "Receipt" },
        { key: "addedBy", label: "Added By" },
        { key: "createdAt", label: "Created At" },
        { key: "action", label: "Action" }
      ]
    };
  },
  computed: {
    sortedData() {
      return [...this.data].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB - dateA;
      });
    }
  },
  methods: {
    formatAmount(item) {
      const employee = this.employeeList.find(emp => emp.uid === item.employeeId) || { basicSalary: 0, extraAllowance: 0 };
      const perDaySalary = calculatePerDaySalary(employee.basicSalary, employee.extraAllowance || 0, this.workingDaysInMonth);
      const perHourSalary = calculatePerHourSalary(perDaySalary, this.workingHours);

      const raw = String(item.amount).trim();
      let value = 0;
      let label = '';

      if (/^\d+(\.\d+)?$/.test(raw)) {
        value = parseFloat(raw);
      } else if (raw.endsWith('x')) {
        const mult = parseInt(raw, 10) || 0;
        value = perHourSalary * mult;
        label = ` (${mult}x)`;
      } else if (/^\d+\s*days?$/i.test(raw)) {
        const mult = parseInt(raw, 10) || 0;
        value = perDaySalary * mult;
        console.log("Calculating days adjustment:", mult, "→", value);
        label = ` (${mult} ${mult === 1 ? 'day' : 'days'})`;
      } else {
        value = parseFloat(raw) || 0;
      }

      const rounded = Math.round(Math.abs(value));
      return `Rs. ${rounded}${label}`;
    },
    formatDate(timestamp) {
      return new Date(timestamp).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    }
  }
};
</script>

<style scoped>
.action-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
}
.edit-btn, .delete-btn {
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
}
.receipt-link {
  color: #435851;
  font-weight: 600;
}
.muted {
  color: #9ca3af;
  font-size: 13px;
}
.edit-btn:hover { background-color: #e0e0e0; }
.delete-btn:hover { background-color: #ffebee; }
.text-ellipsis {  
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 150px;
  display: inline-block;
}
</style>


// WEBPACK FOOTER //
// src/components/PayrollManagement/Adjustments/AdjustmentTable.vue